import React, {Component, ErrorInfo, ReactNode} from "react";
import {Card, CardBody, CardTitle} from "reactstrap";
import {ChartError} from "./types";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: ChartError) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ChartErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Chart Error Boundary caught an error

    this.setState({
      error,
      errorInfo
    });

    // Call the onError callback if provided
    if (this.props.onError) {
      const chartError: ChartError = {
        message: error.message,
        component: "Chart",
        timestamp: Date.now(),
        stack: error.stack
      };
      this.props.onError(chartError);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Card className="chart-error-boundary">
          <CardBody>
            <CardTitle tag="h5" className="text-danger">
              <i className="fa fa-exclamation-triangle mr-2" />
              Chart Error
            </CardTitle>
            <p className="text-muted mb-3">
              Unable to render chart. The data may be invalid or there was an error in the chart component.
            </p>

            {this.props.showDetails && this.state.error && (
              <details className="mb-3">
                <summary className="text-warning cursor-pointer">
                  <small>Error Details</small>
                </summary>
                <div className="mt-2 p-2 bg-light rounded">
                  <small className="text-monospace">
                    <strong>Error:</strong> {this.state.error.message}
                  </small>
                  {this.state.errorInfo && (
                    <div className="mt-1">
                      <small className="text-monospace">
                        <strong>Stack:</strong>
                        <pre className="mt-1 mb-0" style={{fontSize: "10px"}}>
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </small>
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="d-flex justify-content-between align-items-center">
              <button className="btn btn-sm btn-outline-primary" onClick={this.handleRetry}>
                <i className="fa fa-refresh mr-1" />
                Retry
              </button>
            </div>
          </CardBody>
        </Card>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping charts with error boundary
export const withChartErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options: {
    fallback?: ReactNode;
    onError?: (error: ChartError) => void;
    showDetails?: boolean;
  } = {}
) => {
  const ChartWithErrorBoundary = (props: P) => (
    <ChartErrorBoundary {...options}>
      <WrappedComponent {...props} />
    </ChartErrorBoundary>
  );

  ChartWithErrorBoundary.displayName = `withChartErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ChartWithErrorBoundary;
};

// Simple fallback component for charts
export const ChartFallback: React.FC<{
  message?: string;
  height?: number;
}> = ({message = "Chart data unavailable", height = 300}) => (
  <div
    className="d-flex align-items-center justify-content-center bg-light rounded"
    style={{height: `${height}px`, border: "2px dashed #dee2e6"}}
  >
    <div className="text-center text-muted">
      <i className="fa fa-chart-line fa-2x mb-2 d-block" />
      <p className="mb-0">{message}</p>
    </div>
  </div>
);
